import { useQuery } from '@tanstack/react-query'
import type {
  AccessAction,
  FullAccessLog,
  ListAccessLogsResult,
  MaskedAccessLog,
} from '@terafile/shared'
import { listAccessLogs } from '../lib/access-logs-api.js'
import { FETCH_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { classifyFile, iconForKind } from '../lib/file-kind.js'
import { ACCESS_ACTION_LABEL, ACTOR_TYPE_LABEL, LOADING } from '../lib/labels.js'
import { formatRelativeTime } from '../lib/relative-time.js'

/**
 * プロジェクト詳細画面のアクセスログ「最近 N 件」プレビュー。
 *
 * 設計意図:
 *   - 「履歴を開く」だけだと中身が想像できず、開く動機が湧かない
 *   - 直近の動きを 3〜5 件だけ見せることで「気になるログがあるな」→「開いてみよう」の動線を作る
 *   - 全文表示は AccessLogsPage に委ねる（このコンポーネントは preview に徹する）
 *   - キャッシュキーは AccessLogsPage と別にして相互干渉を避ける
 */

type Props = {
  projectId: string
  /** 表示件数。デフォルト 5 件。 */
  limit?: number
}

const ACTION_BADGE_STYLE: Record<AccessAction, string> = {
  VIEW: 'bg-blue-50 text-blue-700 border-blue-200',
  PLAY_START: 'bg-blue-50 text-blue-700 border-blue-200',
  PLAY_PROGRESS: 'bg-blue-50 text-blue-700 border-blue-200',
  PLAY_END: 'bg-blue-50 text-blue-700 border-blue-200',
  DOWNLOAD: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  UPLOAD: 'bg-primary/10 text-primary border-primary/20',
  ARCHIVE: 'bg-gray-50 text-gray-700 border-gray-200',
  UNARCHIVE: 'bg-gray-50 text-gray-700 border-gray-200',
  DELETE: 'bg-red-50 text-red-700 border-red-200',
}

export function AccessLogsPreview({ projectId, limit = 5 }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['access-logs', projectId, 'preview', limit],
    queryFn: () => listAccessLogs(projectId, { limit }),
    enabled: !!projectId,
  })

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>
  }
  if (error) {
    return (
      <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
        {toUserMessage(error, FETCH_ERROR_FALLBACK)}
      </p>
    )
  }
  if (!data || data.items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        まだアクセス記録はありません。共有リンクを発行して相手に送ると、ここに記録が残ります。
      </p>
    )
  }

  return <PreviewList data={data} />
}

function PreviewList({ data }: { data: ListAccessLogsResult }) {
  return (
    <ul className="space-y-2">
      {data.items.map((log) => (
        <li
          key={log.id}
          className="border border-border rounded-md p-3 bg-background flex items-center gap-3 sm:gap-4"
        >
          <ActionBadge action={log.action} />
          <TargetSummary log={log} />
          <ActorSummary log={log} />
          <span className="text-xs text-muted-foreground whitespace-nowrap ml-auto">
            {formatRelativeTime(log.createdAt)}
          </span>
        </li>
      ))}
    </ul>
  )
}

function ActionBadge({ action }: { action: AccessAction }) {
  return (
    <span
      className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${ACTION_BADGE_STYLE[action]}`}
    >
      {ACCESS_ACTION_LABEL[action] ?? action}
    </span>
  )
}

/**
 * 「操作対象」列。ファイル名がある時は種別アイコン + ファイル名、
 * それ以外は共有URL着地・プロジェクト操作などの種別文言で代替する。
 */
function TargetSummary({ log }: { log: FullAccessLog | MaskedAccessLog }) {
  if (log.fileName) {
    const kind = classifyFile(log.fileMimeType ?? '')
    return (
      <span className="flex-1 min-w-0 inline-flex items-center gap-2 text-sm">
        <span aria-hidden className="text-base leading-none shrink-0">
          {iconForKind(kind)}
        </span>
        <span className="truncate">{log.fileName}</span>
      </span>
    )
  }
  if (log.fileId) {
    return (
      <span className="flex-1 min-w-0 text-sm text-muted-foreground italic">
        (削除済みファイル)
      </span>
    )
  }
  if (log.shareLinkId) {
    return (
      <span className="flex-1 min-w-0 text-sm text-muted-foreground">共有リンクへアクセス</span>
    )
  }
  if (log.projectId) {
    return <span className="flex-1 min-w-0 text-sm text-muted-foreground">プロジェクト操作</span>
  }
  return <span className="flex-1 min-w-0 text-sm text-muted-foreground">—</span>
}

/**
 * 「操作した人」の短いサマリ。
 * - viewerEmail があれば優先（共有相手のメール）
 * - なければ actorType ラベル（自分 / 共有相手 / 運営）
 * - 伏せ字 (MaskedAccessLog) の場合は「****」を素直に表示
 */
function ActorSummary({ log }: { log: FullAccessLog | MaskedAccessLog }) {
  const email = log.viewerEmail
  if (email) {
    return (
      <span className="hidden sm:inline text-xs text-muted-foreground truncate max-w-[12rem]">
        {email}
      </span>
    )
  }
  return (
    <span className="hidden sm:inline text-xs text-muted-foreground whitespace-nowrap">
      {ACTOR_TYPE_LABEL[log.actorType] ?? log.actorType}
    </span>
  )
}
